import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { CelebrationDialog } from "@/components/ui/CelebrationDialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const NewsletterSubscription = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    const loadingId = toast.loading("Subscribing...");
    
    try {
      const { data, error } = await supabase.functions.invoke('submit-newsletter', {
        body: { email }
      });

      if (error) {
        console.error('Newsletter submission error:', error);
        
        if (error.message?.includes('409')) {
          toast.error("Already subscribed!", {
            description: "This email is already on our newsletter list."
          });
        } else if (error.message?.includes('429')) {
          toast.error("Too many requests", {
            description: "Please try again in a few minutes."
          });
        } else {
          toast.error("Subscription failed", {
            description: "Please try again or contact support."
          });
        }
        return;
      }

      if (data?.success) {
        setEmail("");
        toast.success("Welcome to Contencraft!", {
          description: "Check your inbox for exclusive content insights."
        });
        setShowCelebration(true);
      }
    } catch (err) {
      console.error('Newsletter submission error:', err);
      toast.error("Subscription failed", {
        description: "Please try again or contact support."
      });
    } finally {
      setIsSubmitting(false);
      toast.dismiss(loadingId);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-background via-background to-primary/10 border-t border-border">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="glass-panel p-8 lg:p-12 rounded-2xl">
            <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Ready to Own Your Content Engine?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Get our weekly insights on building sustainable content operations and creating systems that scale without the ongoing costs.
            </p>
            
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-12 text-base bg-background/50 border-border focus:border-primary"
                required
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="h-12 px-8 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </Button>
            </form>
            
            <p className="text-sm text-muted-foreground mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
      
      <CelebrationDialog
        open={showCelebration}
        onClose={() => setShowCelebration(false)}
        type="newsletter"
      />
    </section>
  );
};

export default NewsletterSubscription;